import { FC } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import { ChartType, TileRepresentation, TileVisualization, TileVisualizationType } from '@common/api';
import { DEFAULT_CHART } from '@components/chart';
import { FormField } from '@components/form';
import {
    AreaChartTile,
    BarChartTile,
    ComboChartTile,
    LineChartTile,
    PieChartTile,
    PinChartTile,
    NumberIcon,
    StackedAreaChartTile,
    StackedBarChartTile,
    SheetTile,
    TableTile,
    WaterfallChartTile,
} from '@components/icons/FarseerIcons';
import { ToggleGroup } from '@components/toggle/ToggleGroup';
import { FlexCol } from '@components/layout/Flex';
import { Update } from '@use-gpu/state';
import { TileDataType } from '@util/enums';

const isTileVisualizationType = (value: unknown): value is TileVisualizationType => {
    return Object.values(TileVisualizationType).includes(value as TileVisualizationType);
};

export type VisualizationTypePickerProps = {
    tile: TileRepresentation;
    onChange: (update: Update<TileRepresentation>) => void;
    canEdit: boolean;
};

export const VisualizationTypePicker: FC<VisualizationTypePickerProps> = (props: VisualizationTypePickerProps) => {
    const { tile, onChange, canEdit } = props;

    const { LL } = useI18nContext();

    const tileVisualization: TileVisualization | null =
        tile.data.type === TileDataType.SHEET_BACKED ? tile.data.visualization : null;

    const tileChartType: ChartType | null =
        tileVisualization && tileVisualization.type === TileVisualizationType.Chart && tileVisualization.chart?.type
            ? tileVisualization.chart?.type
            : null;

    const updateVisualisation = (visualization: Update<TileVisualization>) => {
        onChange({ data: { visualization } });
    };

    const updateChartType = (type: ChartType) => {
        updateVisualisation({
            type: TileVisualizationType.Chart,
            chart: {
                ...DEFAULT_CHART,
                ...tileVisualization?.chart,
                type: type,
            },
        });
    };

    return tileVisualization ? (
        <FlexCol $gap={4}>
            <FormField label={LL.VISUALIZATION_TYPE()}>
                <ToggleGroup
                    $size="small"
                    disabled={!canEdit}
                    value={tileChartType ?? tileVisualization?.type}
                    onChange={(type) =>
                        isTileVisualizationType(type) ? updateVisualisation({ type }) : updateChartType(type)
                    }
                >
                    <ToggleGroup.Toggle label={LL.SHEET()} icon={SheetTile} value={TileVisualizationType.Sheet} />
                    <ToggleGroup.Toggle label={LL.TABLE()} icon={TableTile} value={TileVisualizationType.Table} />
                    <ToggleGroup.Toggle label={LL.BAR_CHART()} icon={BarChartTile} value={ChartType.BarChart} />
                    <ToggleGroup.Toggle
                        label={LL.STACKED_BAR_CHART()}
                        icon={StackedBarChartTile}
                        value={ChartType.StackedBarChart}
                    />
                    <ToggleGroup.Toggle
                        label={LL.WATERFALL_CHART()}
                        icon={WaterfallChartTile}
                        value={ChartType.WaterfallChart}
                    />
                    <ToggleGroup.Toggle label={LL.PIN_CHART()} icon={PinChartTile} value={ChartType.PinChart} />
                    <ToggleGroup.Toggle label={LL.AREA_CHART()} icon={AreaChartTile} value={ChartType.AreaChart} />
                    <ToggleGroup.Toggle
                        label={LL.STACKED_AREA_CHART()}
                        icon={StackedAreaChartTile}
                        value={ChartType.StackedAreaChart}
                    />
                    <ToggleGroup.Toggle label={LL.LINE_CHART()} icon={LineChartTile} value={ChartType.LineChart} />
                    <ToggleGroup.Toggle label={LL.COMBO_CHART()} icon={ComboChartTile} value={ChartType.ComboChart} />
                    <ToggleGroup.Toggle label={LL.PIE_CHART()} icon={PieChartTile} value={ChartType.PieChart} />
                    <ToggleGroup.Toggle
                        label={LL.SINGLE_VALUE()}
                        icon={NumberIcon}
                        value={TileVisualizationType.SingleValue}
                    />
                </ToggleGroup>
            </FormField>
        </FlexCol>
    ) : null;
};
